// SPDX-License-Identifier: FSL-1.1-Apache-2.0
/**
 * @module createConsent
 * @package @geenius/analytics/solidjs-css
 * @description Exposes provider-backed consent state and category helpers for
 * the SolidJS CSS analytics variant.
 */

import type { ConsentSettings } from "@geenius/analytics";
import { AnalyticsContextError } from "@geenius/analytics";
import { useAnalyticsConsent } from "../provider";

export type ConsentScope =
  | "essential"
  | "analytics"
  | "marketing"
  | "personalization";

/**
 * Consent state and actions returned by {@link createConsent}.
 *
 * @property consentSettings Current category-level consent settings.
 * @property updateConsent Updates all consent settings and persists them to the tracker.
 * @property setConsent Alias for `updateConsent` for consumer ergonomics.
 * @property hasConsent Returns whether a specific category is currently granted.
 */
export interface CreateConsentReturn {
  consentSettings: () => ConsentSettings | null;
  updateConsent: (settings: ConsentSettings) => void;
  setConsent: (settings: ConsentSettings) => void;
  hasConsent: (scope: ConsentScope) => boolean;
}

/**
 * Reads and updates analytics consent state from the nearest provider.
 *
 * @returns Consent state, write actions, and category-level helpers.
 * @throws {AnalyticsContextError} When called outside an `<AnalyticsProvider>`.
 */
export function createConsent(): CreateConsentReturn {
  const context = useAnalyticsConsent();

  if (!context) {
    throw new AnalyticsContextError({
      message:
        "createConsent must be called inside an <AnalyticsProvider> tree.",
    });
  }

  return {
    consentSettings: context.consentSettings,
    updateConsent: context.updateConsent,
    setConsent: context.updateConsent,
    hasConsent: (scope: ConsentScope) =>
      context.consentSettings()?.[scope] === true,
  };
}
